<script setup lang="ts">
import type { LocationQuery, RouteParams } from 'vue-router'
import { CtIcon } from '@camelot/components'
import { checkIsWebView, getSafeAreaTopInset, refreshAuthToken } from '@camelot/webview-utils'
import { ref } from 'vue'
import { useConsentManagement } from '~/composables/tracking/consent-management'
import { useDataLayer } from '~/composables/tracking/datalayer'
import { loadTranslations } from '~/composables/translate/translations'

const languageLoaded = ref(false)
const { pushPendingEvents } = useDataLayer()

const isWebview = computed<boolean>(() =>
  checkIsWebView(window),
)

const isEmbeddedInIframe = computed<boolean>(() =>
  window.location !== window.parent.location,
)

async function handleLanguageLoading(params: RouteParams, query: LocationQuery) {
  const lang = params._lang?.toString() || query.locale as string
  languageLoaded.value = await loadTranslations(lang)
}

function initializeTracking(initialLegalEntity: number = 0) {
  // init Consent Manager
  const { isGTMConsentGiven } = useConsentManagement(initialLegalEntity)
  if (isGTMConsentGiven)
    pushPendingEvents()
}

function initializeWebview() {
  if (isWebview.value) {
    document.body.style.paddingTop = `${getSafeAreaTopInset(window)}px`
    document.body.classList.add('webview')

    if (checkIsWebView(window, 'android'))
      document.documentElement.style.setProperty('--safe-area-inset-bottom', '0px')

    // plugins/webview.client.ts already wired window.app at plugin load, so native can only
    // be silent here if it has no valid token cached - ask for a refresh.
    if (useAppStore().accessToken === '') {
      refreshAuthToken(window)
    }
  }
}

onBeforeMount(() => {
  const { params, query } = useRoute()
  if (!isEmbeddedInIframe.value)
    initializeTracking()

  // For Referral, the language is defined as a query parameter instead of a path parameter
  handleLanguageLoading(params, query)
  initializeWebview()
})

onMounted(() => {
})
</script>

<template>
  <div v-if="languageLoaded">
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
  </div>
  <div v-else class="flex justify-center items-center">
    <CtIcon name="loading_web" class="animate-spin w-full h-full basis-1/12" color="transparent" />
  </div>
</template>
